import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { F0TagRaw } from '@factorialco/f0-react'
import { Tabs } from '@factorialco/f0-react/dist/experimental'
import { Link, Pin, Suitcase } from '@factorialco/f0-react/icons/app'
import { FeatureFlagAvailabilitiesDocument } from 'generated/resources/graphql'
import { compact } from 'lodash'

import Loading from 'modules/core/components/Loading'
import Sidepanel from 'modules/core/components/Sidepanel'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { NodeHeader } from 'modules/job_catalog/components/NodeHeader'
import CompetenciesAttributes from 'modules/job_catalog/components/TreeNodeAttributes/CompetenciesAttributes'
import OverviewAttributes from 'modules/job_catalog/components/TreeNodeAttributes/OverviewAttributes'
import WorkingConditionsAttributes from 'modules/job_catalog/components/TreeNodeAttributes/WorkingConditionsAttributes'
import { useJobCatalogTreeVisibility } from 'modules/job_catalog/hooks/useJobCatalogTreeVisibility'
import { useTreeNode } from 'modules/job_catalog/hooks/useTreeNode'
import {
  CategoryType,
  DEFAULT_CATEGORIES,
  NODE_TYPES,
  getCategoryTranslation,
  normalizeNodeType,
} from 'modules/job_catalog/lib/nodesUtils'

const TreeNodePage = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { id } = useParams<{ id: string }>()

  const { canSeeCompetencies } = useJobCatalogTreeVisibility()

  const [activeTab, setActiveTab] = useState<CategoryType | null>(null)
  const [inheritedTooltipOpen, toggleInheritedTooltip] = useToggle(false)

  const {
    nodeName,
    nodeType,
    currentNode,
    currentAttributes,
    salaries,
    childrenInformation,
    workingConditions,
    loading,
  } = useTreeNode(id)

  const { company } = useCurrentSession()
  const { data: featureFlagsData } = useQuery(FeatureFlagAvailabilitiesDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      companyId: company.id,
      featureNames: ['dev_job_catalog_tree_sidepanel'],
    },
  })

  const tabs = useMemo(
    () =>
      compact([
        normalizeNodeType(nodeType) !== NODE_TYPES.LEVEL && DEFAULT_CATEGORIES.OVERVIEW,
        canSeeCompetencies && DEFAULT_CATEGORIES.COMPETENCIES,
      ]),
    [nodeType, canSeeCompetencies]
  )

  useEffect(() => {
    const firstTab = tabs[0]
    if (firstTab) setActiveTab(firstTab)
  }, [tabs])

  const workingConditionsTitle = getCategoryTranslation(
    DEFAULT_CATEGORIES.WORKING_CONDITIONS,
    normalizeNodeType(nodeType)
  )

  if (!id) return null

  const handleTabChange = (tabId: string) => {
    const newTab = tabs.find((tab) => tab === tabId)
    if (newTab) setActiveTab(newTab)
  }

  const useNewJobCatalogVersion =
    featureFlagsData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'dev_job_catalog_tree_sidepanel'
    )?.enabled ?? false

  if (useNewJobCatalogVersion) {
    return (
      <Sidepanel
        open
        onClose={() => navigate(navigation.employees.jobCatalog.overview.fullPath())}
        width="wide"
        title={nodeName ?? ''}
        boxPadding={false}
      >
        {loading ? (
          <Loading expand />
        ) : !currentNode ? null : (
          <>
            <NodeHeader
              name={currentNode.name}
              description={currentNode.description || ''}
              type={currentNode.type}
              childrens={currentNode.children}
              salaries={salaries}
              canEditRole={false}
              currentNode={currentNode}
              isMaximized
            />
            <Box gap="s16" height="full-view-height">
              <Box gap="s16">
                <Box
                  flexDirection="row"
                  alignItems="center"
                  gap="s8"
                  paddingTop="s16"
                  paddingRight="s24"
                  paddingLeft="s24"
                >
                  <Suitcase width={20} height={20} />
                  <Text.Header2 weight="bold" color="grey1000">
                    {workingConditionsTitle}
                  </Text.Header2>
                </Box>
                <Box
                  flexDirection="row"
                  alignItems="center"
                  justifyContent="spaceBetween"
                  paddingTop="s16"
                  paddingRight="s24"
                  paddingBottom="s16"
                  paddingLeft="s24"
                  background="grey100"
                  borderRadius={{
                    all: 'abs016',
                  }}
                  style={{ width: '90%', alignSelf: 'center' }}
                >
                  <Box
                    flexDirection="row"
                    alignItems="center"
                    gap="s8"
                    style={{ maxWidth: '80%' }}
                  >
                    <Text.Normal size="300" color="grey1000" weight="medium">
                      {workingConditions?.name ?? 'Default working conditions'}
                    </Text.Normal>
                    {workingConditions?.inherited && (
                      <Tooltip.Alt
                        attachment="top center"
                        targetAttachment="bottom center"
                        open={inheritedTooltipOpen}
                        toggle={toggleInheritedTooltip}
                        label={i18n.t('job_catalog.nodes.working_conditions.inherited_from', {
                          nodeName:
                            workingConditions?.definedAtNode?.name ?? workingConditions?.definedAtNode?.uuid ??
                            i18n.t('job_catalog.nodes.working_conditions.parent_node'),
                        })}
                      >
                        <Box
                          onMouseEnter={toggleInheritedTooltip(true)}
                          onMouseLeave={toggleInheritedTooltip(false)}
                          style={{ display: 'inline-flex', cursor: 'default' }}
                        >
                          <Link width={16} height={16} />
                        </Box>
                      </Tooltip.Alt>
                    )}
                  </Box>
                  <F0TagRaw text="All Workplaces" icon={Pin} />
                </Box>
              </Box>
            </Box>
          </>
        )}
      </Sidepanel>
    )
  }

  return (
    <Sidepanel
      onClose={() => navigate(navigation.employees.jobCatalog.overview.fullPath())}
      open
      width="wide"
      title={nodeName ?? ''}
      boxPadding={false}
    >
      {loading ? (
        <Loading expand />
      ) : !currentNode ? null : (
        <>
          <NodeHeader
            name={currentNode.name}
            description={currentNode.description || ''}
            type={currentNode.type}
            childrens={currentNode.children}
            salaries={salaries}
            canEditRole={false}
            currentNode={currentNode}
            isMaximized
          />
          <Box gap="s16" height="full-view-height">
            {tabs.length > 0 && (
              <Tabs
                tabs={tabs.map((value) => ({
                  label: getCategoryTranslation(value, normalizeNodeType(nodeType)),
                  id: value,
                }))}
                setActiveTabId={handleTabChange}
                activeTabId={activeTab ?? undefined}
              />
            )}
            <Box height="full">
              {activeTab === DEFAULT_CATEGORIES.COMPETENCIES && (
                <CompetenciesAttributes
                  items={currentAttributes.filter((item) => item.type === activeTab)}
                />
              )}
              {activeTab === DEFAULT_CATEGORIES.WORKING_CONDITIONS && (
                <WorkingConditionsAttributes
                  items={currentAttributes.filter((item) => item.type === activeTab)}
                />
              )}
              {activeTab === DEFAULT_CATEGORIES.OVERVIEW && (
                <OverviewAttributes
                  currentNode={currentNode}
                  childrenInformation={childrenInformation}
                />
              )}
            </Box>
          </Box>
        </>
      )}
    </Sidepanel>
  )
}

export default TreeNodePage
